import { createSelector } from '@reduxjs/toolkit';
import {
    OCR_EXTRACTION_STATUS,
    OCR_TARGET_CONFIG,
    buildOcrFieldView,
    getMacawOcrValues,
    getOcrSubmitIssues,
    getOcrTargets,
    isOcrIdentifierInFlight,
    isOcrVerificationRequired,
    resolveOcrTargetVerification,
    resolveOcrVerificationMode,
} from 'utils/ocrVerification';

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const CARGO_STATUS_FIELD = 'cargo.status';

export const selectOcrAttemptsByTransactionId = (transactionId) => (state) =>
    state.ocrVerification?.attemptsByTransactionId?.[transactionId] ?? EMPTY_ARRAY;

export const selectOcrAttempt = (transactionId, attemptId) => (state) =>
    state.ocrVerification?.attemptsByTransactionId?.[transactionId]?.find(({ id }) => id === attemptId) ?? null;

// One boolean per identifier, so a camera re-renders only when its own read starts or ends.
export const selectOcrIdentifierInFlight = (transactionId, identifier) => (state) =>
    isOcrIdentifierInFlight(state.ocrVerification?.attemptsByTransactionId?.[transactionId], identifier);

// A string, so no memoising is needed; every camera footer reads it.
export const selectOcrVerificationModeByTransactionId = (transactionId) => (state) =>
    resolveOcrVerificationMode(state.forms?.forms?.[transactionId]?.transactionData?.gate);

const selectFormValue = (transactionId, dataField) => (state) => state.forms?.forms?.[transactionId]?.form?.values?.[dataField];

const selectFormEntry = (transactionId, key, dataField) => (state) => state.forms?.forms?.[transactionId]?.form?.[key]?.[dataField];

// Built from the four entries that feed it (Immer keeps their references while they are untouched), so typing in
// another field never recomputes the verification.
const selectMacawValuesByTransactionId = (transactionId) => {
    const { TRAILER, SEAL } = OCR_TARGET_CONFIG;
    return createSelector(
        [
            selectFormEntry(transactionId, 'aiSuggestionHistory', TRAILER.dataField),
            selectFormEntry(transactionId, 'automation', TRAILER.dataField),
            selectFormEntry(transactionId, 'aiSuggestionHistory', SEAL.dataField),
            selectFormEntry(transactionId, 'automation', SEAL.dataField),
        ],
        (trailerHistory, trailerAutomation, sealHistory, sealAutomation) => ({
            [TRAILER.dataField]: getMacawOcrValues(trailerHistory, trailerAutomation),
            [SEAL.dataField]: getMacawOcrValues(sealHistory, sealAutomation),
        })
    );
};

export const selectOcrVerificationsByTransactionId = (transactionId) =>
    createSelector(
        [
            selectOcrVerificationModeByTransactionId(transactionId),
            selectOcrAttemptsByTransactionId(transactionId),
            selectMacawValuesByTransactionId(transactionId),
        ],
        (mode, attempts, macawValues) =>
            getOcrTargets(mode).map((target) =>
                resolveOcrTargetVerification({ target, attempts, macawValues: macawValues[OCR_TARGET_CONFIG[target].dataField] })
            )
    );

/** Everything submit needs: whether OCR is mandatory now, the verifications, and what blocks. */
export const selectOcrSubmitStateByTransactionId = (transactionId) =>
    createSelector(
        [
            selectOcrVerificationModeByTransactionId(transactionId),
            selectOcrVerificationsByTransactionId(transactionId),
            selectFormValue(transactionId, CARGO_STATUS_FIELD),
            selectFormValue(transactionId, OCR_TARGET_CONFIG.TRAILER.dataField),
            selectFormValue(transactionId, OCR_TARGET_CONFIG.SEAL.dataField),
            selectFormEntry(transactionId, 'fieldValueSources', OCR_TARGET_CONFIG.TRAILER.dataField),
            selectFormEntry(transactionId, 'fieldValueSources', OCR_TARGET_CONFIG.SEAL.dataField),
        ],
        (mode, verifications, cargoStatus, trailerNumber, sealNumber, trailerSource, sealSource) => {
            const { TRAILER, SEAL } = OCR_TARGET_CONFIG;
            const required = isOcrVerificationRequired(mode, cargoStatus);
            const fieldValues = { [TRAILER.dataField]: trailerNumber, [SEAL.dataField]: sealNumber };
            const fieldValueSources = { [TRAILER.dataField]: trailerSource, [SEAL.dataField]: sealSource };
            const issues = getOcrSubmitIssues({ required, verifications, fieldValues, fieldValueSources });
            return { mode, required, verifications, fieldValues, fieldValueSources, issues };
        }
    );

/** `{ [dataField]: view }` for the verified fields that have OCR reads; see buildOcrFieldView. */
export const selectOcrFieldViewsByTransactionId = (transactionId) =>
    createSelector(
        [
            selectOcrVerificationsByTransactionId(transactionId),
            selectOcrAttemptsByTransactionId(transactionId),
            selectMacawValuesByTransactionId(transactionId),
        ],
        (verifications, attempts, macawValues) => {
            const views = {};
            verifications.forEach((verification) => {
                const { dataField } = OCR_TARGET_CONFIG[verification.target];
                const view = buildOcrFieldView({ verification, attempts, macawValues: macawValues[dataField] });
                if (view) views[dataField] = view;
            });
            return Object.keys(views).length ? views : EMPTY_OBJECT;
        }
    );

// The identifiers being read, as a sorted string, so a camera footer re-renders only when that set changes.
export const selectOcrInFlightIdentifiersKey = (transactionId) => (state) => {
    if (transactionId == null) return '';
    const attempts = state.ocrVerification?.attemptsByTransactionId?.[transactionId];
    if (!attempts?.length) return '';
    return attempts
        .filter(({ status }) => status === OCR_EXTRACTION_STATUS.PENDING)
        .map(({ identifier }) => identifier)
        .sort()
        .join(',');
};
